import axios from 'axios';

import type {
  IncomeProtectionDaSettingCreationType,
  IncomeProtectionDaSettingUpdateType,
  IncomeProtectionPaymentsSummaryResponse,
  MedicalRecordCreationType,
  MedicalRecordUpdateType,
} from './types';

export const getIncomeProtectionDaBaseUrl = ({
  organizationId,
  claimId,
  exposureId,
}: {
  organizationId: number;
  claimId: number;
  exposureId: number;
}) => `/api/v1/organizations/${organizationId}/income_protection/claims/${claimId}/exposures/${exposureId}`;

export const getIncomeProtectionSettingUrl = ({
  organizationId,
  claimId,
  exposureId,
}: {
  organizationId: number;
  claimId: number;
  exposureId: number;
}) => `${getIncomeProtectionDaBaseUrl({ organizationId, claimId, exposureId })}/da_settings`;

export const getIncomeProtectionDaFullDetailsUrl = ({
  organizationId,
  claimId,
  exposureId,
}: {
  organizationId: number;
  claimId: number;
  exposureId: number;
}) => `${getIncomeProtectionDaBaseUrl({ organizationId, claimId, exposureId })}/da_details`;

export const createIncomeProtectionDaSetting = async ({
  organizationId,
  claimId,
  exposureId,
  data,
}: {
  organizationId: number;
  claimId: number;
  exposureId: number;
  data: IncomeProtectionDaSettingCreationType;
}): Promise<void> => {
  const url = getIncomeProtectionSettingUrl({ organizationId, claimId, exposureId });
  await axios.post(url, data);
};

export const updateIncomeProtectionDaSetting = async ({
  organizationId,
  claimId,
  exposureId,
  data,
}: {
  organizationId: number;
  claimId: number;
  exposureId: number;
  data: IncomeProtectionDaSettingUpdateType;
}): Promise<void> => {
  const url = getIncomeProtectionSettingUrl({ organizationId, claimId, exposureId });
  await axios.put(url, data);
};

// Medical Records API
export const getMedicalRecordsUrl = ({
  organizationId,
  claimId,
  exposureId,
}: {
  organizationId: number;
  claimId: number;
  exposureId: number;
}) =>
  `/api/v1/organizations/${organizationId}/income_protection/claims/${claimId}/exposures/${exposureId}/medical_records`;

export const createMedicalRecord = async ({
  organizationId,
  claimId,
  exposureId,
  data,
}: {
  organizationId: number;
  claimId: number;
  exposureId: number;
  data: MedicalRecordCreationType;
}): Promise<{ id: number }> => {
  const url = getMedicalRecordsUrl({ organizationId, claimId, exposureId });
  const response = await axios.post(url, data);
  return response.data;
};

export const updateMedicalRecord = async ({
  organizationId,
  claimId,
  exposureId,
  recordId,
  data,
}: {
  organizationId: number;
  claimId: number;
  exposureId: number;
  recordId: number;
  data: MedicalRecordUpdateType;
}): Promise<void> => {
  const url = `${getMedicalRecordsUrl({ organizationId, claimId, exposureId })}/${recordId}`;
  await axios.put(url, data);
};

export const deleteMedicalRecord = async ({
  organizationId,
  claimId,
  exposureId,
  recordId,
}: {
  organizationId: number;
  claimId: number;
  exposureId: number;
  recordId: number;
}): Promise<void> => {
  const url = `${getMedicalRecordsUrl({ organizationId, claimId, exposureId })}/${recordId}`;
  await axios.delete(url);
};

export const setMedicalDocumentNotification = async ({
  organizationId,
  claimId,
  exposureId,
  dueDate,
}: {
  organizationId: number;
  claimId: number;
  exposureId: number;
  dueDate: string;
}): Promise<{ id: number } | null> => {
  const url = `${getIncomeProtectionSettingUrl({ organizationId, claimId, exposureId })}/medical_document_notification`;
  const response = await axios.post(url, { due_date: dueDate });
  // Backend returns 204 when notification type is disabled for the organization
  if (response.status === 204) {
    return null;
  }
  return response.data;
};

export const createPaymentNotification = async ({
  organizationId,
  claimId,
  exposureId,
  dueDate,
}: {
  organizationId: number;
  claimId: number;
  exposureId: number;
  dueDate: string;
}): Promise<{ id: number } | null> => {
  const url = `${getIncomeProtectionSettingUrl({ organizationId, claimId, exposureId })}/payment_notification`;
  const response = await axios.post<{ id: number } | null>(url, { due_date: dueDate });

  // Backend returns 204 when notification type is disabled for the organization
  if (response.status === 204) {
    return null;
  }
  return response.data;
};

export const getIncomeProtectionPaymentSummary = async ({
  organizationId,
  claimId,
  exposureId,
}: {
  organizationId: number;
  claimId: number;
  exposureId: number;
}): Promise<IncomeProtectionPaymentsSummaryResponse> => {
  const url = `${getIncomeProtectionDaBaseUrl({ organizationId, claimId, exposureId })}/payments_summary`;
  const response = await axios.get<IncomeProtectionPaymentsSummaryResponse>(url);
  return response.data;
};
